import { useCallback, useMemo, useState } from "react";
import { SortingState, Updater } from "@tanstack/react-table";
import { PAGE_SIZE_OPTIONS } from "./TablePagination";

const DEFAULT_SORTING: SortingState = [{ id: "created_on", desc: true }];
const DEFAULT_PAGE_SIZE = 10;

const resolveUpdater = <T,>(updater: Updater<T>, previous: T): T =>
  typeof updater === "function" ? (updater as (old: T) => T)(previous) : updater;

/**
 * `defaultSorting` and `initialPageSize` seed React state directly, so a
 * restored view is the very first render rather than applied via an effect
 * after it - that distinction is what keeps a restored view to a single
 * list request instead of one for the default and one for the restore.
 */
export const useServerTable = (
  defaultSorting: SortingState = DEFAULT_SORTING,
  initialPageSize: number = DEFAULT_PAGE_SIZE,
  onSortChange?: (sorting: SortingState) => void,
  onPageSizeChange?: (pageSize: number) => void
) => {
  const seededPageSize = PAGE_SIZE_OPTIONS.includes(initialPageSize)
    ? initialPageSize
    : DEFAULT_PAGE_SIZE;

  const [{ pageIndex, pageSize }, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: seededPageSize,
  });
  const [sorting, setSortingState] = useState<SortingState>(defaultSorting);

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize]
  );

  const setPagination = useCallback(
    (updater: Updater<{ pageIndex: number; pageSize: number }>) => {
      setPaginationState((previous) => {
        const next = resolveUpdater(updater, previous);
        if (next.pageSize !== previous.pageSize) {
          onPageSizeChange?.(next.pageSize);
        }
        return next;
      });
    },
    [onPageSizeChange]
  );

  const setSorting = useCallback(
    (updater: Updater<SortingState>) => {
      setSortingState((previous) => {
        const next = resolveUpdater(updater, previous);
        onSortChange?.(next);
        return next;
      });
    },
    [onSortChange]
  );

  // Page index resets to page one on a filter/search change (see the
  // effect in each table component); page size is part of the persisted
  // view and must survive that reset unchanged, so this only touches
  // pageIndex - it does not go through setPagination and so does not
  // report a page-size change either.
  const resetPagination = useCallback(
    () => setPaginationState((previous) => ({ ...previous, pageIndex: 0 })),
    []
  );

  return {
    pageIndex,
    pageSize,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortOrder: (sorting[0]?.desc ? "dsc" : "asc") as "dsc" | "asc",
    sortBy: sorting[0]?.id,
    resetPagination,
  };
};
